// Block entities: chest / furnace / spawner (M6.2; §3.6, §5.5 phase 6). PURE (ENG-1).
//
// A block entity is per-position state that a plain block-state id can't hold —
// a chest's items, a furnace's smelt progress, a spawner's countdown. The store
// keys them by world (x,y,z); it is ticked in phase 6 (furnaces cook, spawners
// fire) and serialized to the `blockEntities` store (§3.6). Container logic lives
// beside this file (containers/chest.ts, containers/furnace.ts); the spawner is
// small enough to keep here.
import { SPAWNER, SCR } from '../config';
import type { AutopilotPreset } from '../aviation/autopilot';
import type { Registries } from '../registry/load';
import type { Rng } from '../engine/rng';
import { type ItemStack, isEmpty, cloneStack } from './inventory';
import {
  type ChestData, newChest, chestContents, cloneChest,
} from './containers/chest';
import {
  type FurnaceData, newFurnace, furnaceContents, tickFurnace,
} from './containers/furnace';
import {
  type ArrayContainer, newArrayContainer, arrayContents, cloneArrayContainer,
} from './containers/array';
import {
  type CampfireData, newCampfire, campfireContents, cloneCampfire, tickCampfire,
} from './containers/campfire';
import {
  type BrewingData, newBrewing, brewingContents, cloneBrewing, tickBrewing,
} from './containers/brewing';
import { stableLineId, type TaskLine } from '../coworking/text';
import { justFinished, type TimerData } from '../coworking/timer';

export interface SpawnerData {
  /** Mob id this spawner produces. */
  mob: string;
  /** Ticks until the next firing. */
  delay: number;
}

/** A `script_block`'s persisted state (SCR-4, M35): its source text plus an optional
 *  editable autopilot preset. When `autopilot` is set, a trigger engages the aircraft
 *  autopilot with those settings instead of running `source`. */
export interface ScriptData {
  /** Source struct version (E-33; a newer version is rejected gracefully on load). */
  version: number;
  /** The script source (SCR-1 language). */
  source: string;
  /** The editable AVI-3 autopilot preset, or null when the block runs its script. */
  autopilot: AutopilotPreset | null;
}

/** A `world_door`'s persisted state (DOR-1, M41): a directed edge in the world graph.
 *  `doorId` is this door's stable identity (assigned by the client at bind time, '' while
 *  unbound); `targetWorldId`/`targetDoorId` name the paired door it traverses to. An
 *  unbound door (empty `targetWorldId`) is a node with no outgoing edge. The sim only
 *  stores + serializes these strings — all graph/traversal logic is client-side (ENG-1). */
export interface WorldDoorData {
  doorId: string;
  targetWorldId: string;
  targetDoorId: string;
}

export type BlockEntityType = 'chest' | 'furnace' | 'smoker' | 'campfire' | 'spawner' | 'hopper' | 'dropper' | 'dispenser' | 'crafter' | 'brewing_stand' | 'script' | 'world_door' | 'decorated_pot' | 'crop' | 'sticky_note' | 'task_board' | 'timer';

/** A sticky note (M55.1, ARCH §30.3, COW-2): a single capped line. */
export interface StickyNoteData {
  text: string;
}
/** A task board (M55.1, ARCH §30.3, COW-3): a capped list of lines. */
export interface TaskBoardData {
  lines: TaskLine[];
}

/** A genetic crop's seed genome (HER-8, ARCH §28.10): base64 in the crop table's layout.
 *  Created only when the heredity rule is on at planting (E-55); removed with the block. */
export interface CropData {
  genome: string;
}

/** Slot counts for the array-backed logistics containers (M15.2.1). */
export const HOPPER_SIZE = 5;
export const DISPENSER_SIZE = 9; // dropper + dispenser are both a 3×3 grid
export const CRAFTER_SIZE = 9; // the auto-crafter's 3×3 grid (MB)
export const POT_SIZE = 1; // a decorated pot holds a single stack (M42)

/** A crafter's 3×3 grid plus its per-slot disabled flags (forced-gap shaping). */
export interface CrafterData extends ArrayContainer { disabled: boolean[] }

interface BaseBE {
  x: number;
  y: number;
  z: number;
}
export interface ChestBE extends BaseBE { type: 'chest'; data: ChestData }
export interface FurnaceBE extends BaseBE { type: 'furnace'; data: FurnaceData }
/** A smoker: a furnace that only cooks food, at twice the speed (MB.3). */
export interface SmokerBE extends BaseBE { type: 'smoker'; data: FurnaceData }
/** A campfire: a fuel-less 4-slot food cooker that pops its results (MB.3). */
export interface CampfireBE extends BaseBE { type: 'campfire'; data: CampfireData }
export interface SpawnerBE extends BaseBE { type: 'spawner'; data: SpawnerData }
export interface HopperBE extends BaseBE { type: 'hopper'; data: ArrayContainer }
export interface DropperBE extends BaseBE { type: 'dropper'; data: ArrayContainer }
export interface DispenserBE extends BaseBE { type: 'dispenser'; data: ArrayContainer }
/** A crafter: a redstone-pulsed 3×3 auto-crafting grid (MB). */
export interface CrafterBE extends BaseBE { type: 'crafter'; data: CrafterData }
/** A brewing stand: 3 bottles + ingredient + blaze-powder fuel (MB.4). */
export interface BrewingStandBE extends BaseBE { type: 'brewing_stand'; data: BrewingData }
/** A script block: the creative/ops command-block successor (SCR-4, M35). */
export interface ScriptBE extends BaseBE { type: 'script'; data: ScriptData }
/** A world door: a TARDIS edge to another world in the save graph (DOR-1, M41). */
export interface WorldDoorBE extends BaseBE { type: 'world_door'; data: WorldDoorData }
/** A decorated pot: a single-stack store crafted from pottery sherds (M42). */
export interface DecoratedPotBE extends BaseBE { type: 'decorated_pot'; data: ArrayContainer }
export interface CropBE extends BaseBE { type: 'crop'; data: CropData }
/** A sticky note, wall-mounted (M55.1). */
export interface StickyNoteBE extends BaseBE { type: 'sticky_note'; data: StickyNoteData }
/** A task board (M55.1). */
export interface TaskBoardBE extends BaseBE { type: 'task_board'; data: TaskBoardData }
/** A shared countdown timer, counting down from the sim clock (M55.1). */
export interface TimerBE extends BaseBE { type: 'timer'; data: TimerData }
export type BlockEntity = ChestBE | FurnaceBE | SmokerBE | CampfireBE | SpawnerBE | HopperBE | DropperBE | DispenserBE | CrafterBE | BrewingStandBE | ScriptBE | WorldDoorBE | DecoratedPotBE | CropBE | StickyNoteBE | TaskBoardBE | TimerBE;

/** Which block names carry which block entity — the data-driven `blockEntity`
 *  field on the block registry drives creation on place / structure gen. */
export function newBlockEntityData(type: BlockEntityType): BlockEntity['data'] {
  switch (type) {
    case 'chest': return newChest();
    case 'furnace': case 'smoker': return newFurnace();
    case 'campfire': return newCampfire();
    case 'spawner': return { mob: 'zombie', delay: SPAWNER.MIN_DELAY };
    case 'hopper': return newArrayContainer(HOPPER_SIZE);
    case 'dropper': case 'dispenser': return newArrayContainer(DISPENSER_SIZE);
    case 'crafter': return { ...newArrayContainer(CRAFTER_SIZE), disabled: Array<boolean>(CRAFTER_SIZE).fill(false) };
    case 'brewing_stand': return newBrewing();
    case 'script': return { version: SCR.SOURCE_VERSION, source: '', autopilot: null };
    case 'world_door': return { doorId: '', targetWorldId: '', targetDoorId: '' };
    case 'decorated_pot': return newArrayContainer(POT_SIZE);
    case 'crop': return { genome: '' };
    case 'sticky_note': return { text: '' };
    case 'task_board': return { lines: [] };
    case 'timer': return { endTick: null, preset: 'pomodoro25' };
  }
}

/** Items a broken block entity scatters (its stored contents). */
export function blockEntityDrops(be: BlockEntity): ItemStack[] {
  if (be.type === 'chest') return chestContents(be.data);
  if (be.type === 'furnace' || be.type === 'smoker') return furnaceContents(be.data);
  if (be.type === 'campfire') return campfireContents(be.data);
  if (be.type === 'hopper' || be.type === 'dropper' || be.type === 'dispenser') return arrayContents(be.data);
  if (be.type === 'crafter') return arrayContents(be.data);
  if (be.type === 'decorated_pot') return arrayContents(be.data);
  if (be.type === 'brewing_stand') return brewingContents(be.data);
  return [];
}

const key = (x: number, y: number, z: number): string => `${x},${y},${z}`;

/** A persisted block entity row (§3.6). */
export interface SavedBlockEntity {
  type: BlockEntityType;
  x: number;
  y: number;
  z: number;
  data: unknown;
}

export class BlockEntityStore {
  private readonly map = new Map<string, BlockEntity>();
  /** Live count per type, maintained by every mutator below.
   *
   *  It exists for ONE question asked on a very hot path: "are there any script blocks
   *  in this world at all?" §27.9's neighbour bus notifies the 6 faces of every edit,
   *  and flowing water alone edits thousands of cells a tick — so the notification
   *  consumer must be able to rule itself out in O(1). Doing it with a `get()` per
   *  notification instead builds a `"x,y,z"` key string per notification and is enough
   *  to hang the tick loop outright (D-TF-222, D-TF-226). */
  private readonly counts = new Map<BlockEntityType, number>();

  get size(): number { return this.map.size; }

  /** How many block entities of `type` exist. O(1). */
  countOf(type: BlockEntityType): number {
    return this.counts.get(type) ?? 0;
  }

  private bump(type: BlockEntityType, by: number): void {
    const n = (this.counts.get(type) ?? 0) + by;
    if (n > 0) this.counts.set(type, n);
    else this.counts.delete(type);
  }

  get(x: number, y: number, z: number): BlockEntity | undefined {
    return this.map.get(key(x, y, z));
  }

  set(be: BlockEntity): void {
    const k = key(be.x, be.y, be.z);
    const prev = this.map.get(k);
    if (prev) this.bump(prev.type, -1);   // `set` REPLACES, so the old one is gone
    this.map.set(k, be);
    this.bump(be.type, 1);
  }

  /** Create a fresh block entity of `type` at (x,y,z) unless one already exists. */
  create(type: BlockEntityType, x: number, y: number, z: number): BlockEntity {
    const existing = this.map.get(key(x, y, z));
    if (existing) return existing;
    const be = { type, x, y, z, data: newBlockEntityData(type) } as BlockEntity;
    this.map.set(key(x, y, z), be);
    this.bump(type, 1);
    return be;
  }

  remove(x: number, y: number, z: number): BlockEntity | undefined {
    const k = key(x, y, z);
    const be = this.map.get(k);
    if (be) { this.map.delete(k); this.bump(be.type, -1); }
    return be;
  }

  values(): IterableIterator<BlockEntity> {
    return this.map.values();
  }

  clear(): void { this.map.clear(); this.counts.clear(); }

  toJSON(): SavedBlockEntity[] {
    const out: SavedBlockEntity[] = [];
    for (const be of this.map.values()) out.push({ type: be.type, x: be.x, y: be.y, z: be.z, data: serializeData(be) });
    return out;
  }

  loadJSON(list: readonly SavedBlockEntity[] | undefined): void {
    if (!list) return;
    for (const row of list) {
      const data = row.type === 'task_board' ? migrateTaskBoard(row.x, row.y, row.z, row.data) : row.data;
      const be = { type: row.type, x: row.x, y: row.y, z: row.z, data } as BlockEntity;
      this.map.set(key(row.x, row.y, row.z), be);
    }
  }
}

function serializeData(be: BlockEntity): unknown {
  // Chest items alias live ItemStacks — clone so a later mutation can't corrupt
  // an in-flight save. Furnace/spawner data are plain and cloned structurally.
  if (be.type === 'chest') return cloneChest(be.data);
  if (be.type === 'campfire') return cloneCampfire(be.data);
  if (be.type === 'hopper' || be.type === 'dropper' || be.type === 'dispenser') return cloneArrayContainer(be.data);
  if (be.type === 'crafter') return { ...cloneArrayContainer(be.data), disabled: [...be.data.disabled] };
  if (be.type === 'brewing_stand') return cloneBrewing(be.data);
  if (be.type === 'script') return { version: be.data.version, source: be.data.source, autopilot: be.data.autopilot ? { ...be.data.autopilot } : null };
  if (be.type === 'furnace' || be.type === 'smoker') {
    const f = be.data;
    return {
      input: cloneStackOrEmpty(f.input), fuel: cloneStackOrEmpty(f.fuel), output: cloneStackOrEmpty(f.output),
      burnTime: f.burnTime, burnTotal: f.burnTotal, cookTime: f.cookTime, cookTotal: f.cookTotal, xp: f.xp,
    } satisfies FurnaceData;
  }
  return { ...be.data };
}

function cloneStackOrEmpty(s: ItemStack): ItemStack {
  return isEmpty(s) ? { item: 0, count: 0 } : cloneStack(s);
}

/**
 * M107.2 (ARCH §39.7, save version +1) — a `task_board` saved before this version has
 * lines with no `id`. Assigns one deterministically from `(x,y,z,index)` via
 * `stableLineId`, so re-running this migration on the same un-migrated row (the save
 * never having been re-written) lands on the same ids every time. Forgiving of
 * anything else malformed in `raw`, the same E-63 "truncate, don't refuse" instinct
 * `normalizeBoard` already applies.
 */
function migrateTaskBoard(x: number, y: number, z: number, raw: unknown): TaskBoardData {
  const rawLines = (raw as { lines?: unknown } | null)?.lines;
  if (!Array.isArray(rawLines)) return { lines: [] };
  const lines: TaskLine[] = rawLines.map((entry, index) => {
    const l = entry as Partial<TaskLine> | null;
    return {
      id: stableLineId(x, y, z, index, l?.id),
      text: typeof l?.text === 'string' ? l.text : '',
      done: l?.done === true,
    };
  });
  return { lines };
}

// ── Phase-6 tick (furnaces cook, spawners fire) ──────────────────────────────
export interface SpawnerEnv {
  /** Distance (blocks) to the nearest player from (x,y,z); Infinity if none. */
  nearestPlayerDist(x: number, y: number, z: number): number;
  /** Count of live `type` mobs within `radius` of (x,y,z). */
  countMobsNear(type: string, x: number, y: number, z: number, radius: number): number;
  /** Spawn one `type` mob at (x,y,z); returns true on success. */
  spawnMob(type: string, x: number, y: number, z: number): boolean;
  /** A 2-high air gap with a floor at feet-Y y (spawner placement check). */
  canSpawnAt(x: number, y: number, z: number): boolean;
  rng: Rng;
}

export interface BlockEntityEnv {
  reg: Registries;
  maxStackOf: (item: number) => number;
  spawner?: SpawnerEnv;
  /** Spawn a finished item as a world drop above (x,y,z) — campfires pop food. */
  dropItem?: (x: number, y: number, z: number, stack: ItemStack) => void;
  /** The world's current tick — a running `timer` compares against it (M55.4,
   *  ARCH §30.3: "counts down from the sim clock", never a sync message). */
  tick?: number;
  /** A running timer just reached zero THIS tick (M55.4) — the host announces
   *  `Timer finished.` to every player within `COW.TIMER_RADIUS`. */
  onTimerFinished?: (x: number, y: number, z: number) => void;
}

/** Advance every block entity one tick (§5.5 phase 6). */
export function tickBlockEntities(store: BlockEntityStore, env: BlockEntityEnv): void {
  for (const be of store.values()) {
    if (be.type === 'furnace') {
      tickFurnace(be.data, env.reg, env.maxStackOf);
    } else if (be.type === 'smoker') {
      tickFurnace(be.data, env.reg, env.maxStackOf, ['smoking']); // food only, 2× via 100gt recipes
    } else if (be.type === 'campfire') {
      const done = tickCampfire(be.data, env.reg);
      if (env.dropItem) for (const s of done) env.dropItem(be.x, be.y, be.z, s);
    } else if (be.type === 'brewing_stand') {
      tickBrewing(be.data, env.reg);
    } else if (be.type === 'spawner' && env.spawner) {
      tickSpawner(be, env.spawner);
    } else if (be.type === 'timer' && env.tick !== undefined && justFinished(be.data, env.tick)) {
      env.onTimerFinished?.(be.x, be.y, be.z);
    }
  }
}

/** One spawner's countdown + firing (MC-style: only while a player is in range). */
export function tickSpawner(be: SpawnerBE, env: SpawnerEnv): void {
  const { x, y, z } = be;
  if (env.nearestPlayerDist(x, y, z) > SPAWNER.REQUIRED_PLAYER_RANGE) return;
  if (be.data.delay > 0) { be.data.delay--; return; }
  be.data.delay = SPAWNER.MIN_DELAY + env.rng.nextInt(SPAWNER.MAX_DELAY - SPAWNER.MIN_DELAY + 1);

  const R = SPAWNER.SPAWN_RANGE;
  if (env.countMobsNear(be.data.mob, x, y, z, R * 2) >= SPAWNER.MAX_NEARBY) return;

  for (let i = 0; i < SPAWNER.SPAWN_COUNT; i++) {
    if (env.countMobsNear(be.data.mob, x, y, z, R * 2) >= SPAWNER.MAX_NEARBY) break;
    const ox = x + env.rng.nextInt(R * 2 + 1) - R;
    const oz = z + env.rng.nextInt(R * 2 + 1) - R;
    const oy = y + env.rng.nextInt(3) - 1;
    if (env.canSpawnAt(ox, oy, oz)) env.spawnMob(be.data.mob, ox + 0.5, oy, oz + 0.5);
  }
}
